import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import { QuestionnaireForm } from "@/components/questionnaire/QuestionnaireForm.tsx";
import { QuestionnaireProvider } from "@/components/questionnaire/QuestionnaireProvider.tsx";
import { QuestionnaireHandle } from "@/components/questionnaire/questionnaire.types.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { PerformanceReviewCompleted } from "@/pages/employees/performance-reviews/components/PerformanceReviewCompleted.tsx";
import { PerformanceReviewStart } from "@/pages/employees/performance-reviews/components/PerformanceReviewStart.tsx";
import { usePerformanceReviewRespond } from "@/pages/employees/performance-reviews/performance-review-respond.tsx";
import { EmployeeViewModal } from "@/pages/employees/widgets/EmployeeViewModal.tsx";
import { useEmployeePerformanceReviewByReviewerId } from "@/queries/employees/use-employee-performance-review.query.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiFormParticipantStatus, ApiFormType } from "@/types/forms/form.types.ts";
import { ApiPerformanceReviewStatus } from "@/types/performance-review.types.ts";
import { Button, FullScreenPage, Stack, Text, Title, useModals } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { useEffectOnce } from "react-use";
import { useRef, useState } from "react";

export const PerformanceReviewRespond = () => {
  const { reviewer_id } = useParams();
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const { openModal } = useModals();
  const { exit } = usePerformanceReviewRespond();
  const questionnaireRef = useRef<QuestionnaireHandle>(null);

  const handleExit = async () => {
    await questionnaireRef.current?.saveDraft();
    exit();
  };

  const { data: employeeMe } = useEmployeeMe();
  const { data: review } = useEmployeePerformanceReviewByReviewerId({
    employeeId: employeeMe?.id || 0,
    reviewerId: Number(reviewer_id),
    enabled: !!employeeMe,
  });
  const location = useLocation();
  const initialForcedState = (
    location.state as {
      forcedReviewerStatus?: ApiFormParticipantStatus;
    } | null
  )?.forcedReviewerStatus;
  const [forcedState, setForcedState] = useState<ApiFormParticipantStatus | undefined>(
    initialForcedState
  );

  useTitle(review ? review.title : undefined);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.performanceReviews.responseFormViewed, {
      [analyticProperties.id]: reviewer_id,
    });
  });

  if (!review) {
    return <FullScreenPage isCenterVertical={false}></FullScreenPage>;
  }

  if (review) {
    const reviewer_status = forcedState || review.reviewer_status;
    const review_status =
      forcedState == ApiFormParticipantStatus.in_progress
        ? ApiPerformanceReviewStatus.running
        : review.status;

    if (
      reviewer_status == ApiFormParticipantStatus.invited ||
      reviewer_status == ApiFormParticipantStatus.added
    ) {
      return <PerformanceReviewStart review={review} />;
    } else if (
      review_status == ApiPerformanceReviewStatus.completed ||
      reviewer_status == ApiFormParticipantStatus.completed
    ) {
      return (
        <PerformanceReviewCompleted
          review={review}
          onEditAnswers={() => setForcedState(ApiFormParticipantStatus.in_progress)}
        />
      );
    }
  }

  return (
    <>
      <FullScreenPage
        backSlot={
          <Button variant={"secondary"} size={"lg"} onClick={handleExit}>
            {t("common:exit")}
          </Button>
        }
        isCenterVertical={false}
      >
        <QuestionnaireProvider
          ref={questionnaireRef}
          type={ApiFormType.performanceReview}
          id={reviewer_id}
          onAfterSubmit={() => {
            if (forcedState) setForcedState(undefined);

            analyticsService.trackEvent(analyticEvents.performanceReviews.responseCompleted, {
              [analyticProperties.id]: reviewer_id,
            });
          }}
        >
          <QuestionnaireForm />
        </QuestionnaireProvider>
        <Stack className={"lg:fixed lg:pt-45 pt-10 right-0 top-0 pr-6 pb-4"} gap={"xl"}>
          {review.participant && (
            <Stack
              gap={"smd"}
              items={"start"}
              className={"cursor-pointer"}
              onClick={() => {
                openModal(EmployeeViewModal, {
                  employeeId: review.participant.id,
                });
              }}
            >
              <EmployeeAvatar employee={review.participant} size={"2xl"} />
              <Title
                header={t("scorecards:show_profile", {
                  applicant: renderEmployeeName(review.participant),
                })}
                className={"max-w-40"}
              />
            </Stack>
          )}
          {review.comments &&
            review.comments.map((comment) => (
              <Stack
                className={"p-4 rounded-xs bg-dark/5 border-dark/5 w-40"}
                gap={"sm"}
                key={`comment-${comment.id}`}
              >
                <EmployeeItem
                  employee={comment.author}
                  size={"sm"}
                  showPosition={false}
                  variant={"secondary"}
                />
                <Text>{comment.text}</Text>
              </Stack>
            ))}
        </Stack>
      </FullScreenPage>
    </>
  );
};
